import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import TextareaAutosize from "react-textarea-autosize";
import { createStation, updateStation } from "../services/stationsService";
import "../styles/components/form.css";
import "../styles/components/station_form.css";
import CloseIcon from "../assets/icons/close-icon.svg";
import Loader from "../components/Loader";

const StationForm = ({ onClose, initialData, mode, onSuccess }) => {
    const [gsSerial, setGsSerial] = useState(initialData ? initialData.gs_serial : "");
    const [city, setCity] = useState(initialData ? initialData.city : "");
    const [address, setAddress] = useState(initialData ? initialData.address : "");
    const [isStoreAvailable, setIsStoreAvailable] = useState(initialData ? initialData.is_store_available : false);
    const [isLpgAvailable, setIsLpgAvailable] = useState(initialData ? initialData.is_lpg_available : false);
    const [isBpowerAvailable, setIsBpowerAvailable] = useState(initialData ? initialData.is_bpower_available : false);
    const [is100Available, setIs100Available] = useState(initialData ? initialData.is_100_available : false);
    const [isAi95Available, setIsAi95Available] = useState(initialData ? initialData.is_ai95_available : false);
    const [isAi92Available, setIsAi92Available] = useState(initialData ? initialData.is_ai92_available : false);
    const [isDtAvailable, setIsDtAvailable] = useState(initialData ? initialData.is_dt_available : false);
    const [street, setStreet] = useState(initialData ? initialData.street : "");
    const [streetNumber, setStreetNumber] = useState(initialData ? initialData.street_number : "");
    const [latitude, setLatitude] = useState(initialData ? initialData.latitude : "");
    const [longitude, setLongitude] = useState(initialData ? initialData.longitude : "");
    const [gasStationService, setGasStationService] = useState(initialData ? initialData.gasStationService : {
        frenchDog: false,
        coffee: false,
        bakery: false,
        coffee_zone: false,
        terminal: false,
        atm: false,
        bicycleCorner: false,
        plasticDispose: false,
        batteryDispose: false,
        bookTrade: false,
        takeClothes: false,
        vulcanization: false,
        toilet: false,
        placeForPraying: false,
        condomate: false,
        socGs: false,
        kfcAuto: false,
        freeWifi: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const isFormValid = gsSerial.trim() && city.trim() && address.trim() && street.trim() && streetNumber.trim() && latitude && longitude;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const requestData = {
                gasStation: {
                    gs_serial: gsSerial,
                    city,
                    address,
                    is_store_available: String(isStoreAvailable),
                    is_lpg_available: String(isLpgAvailable),
                    is_bpower_available: String(isBpowerAvailable),
                    is_100_available: String(is100Available),
                    is_ai95_available: String(isAi95Available),
                    is_ai92_available: String(isAi92Available),
                    is_dt_available: String(isDtAvailable),
                    region: { id: 1 },
                    street,
                    street_number: streetNumber,
                    latitude: parseFloat(latitude) || 0,
                    longitude: parseFloat(longitude) || 0
                },
                gasStationServices: {
                    frenchDog: String(gasStationService.frenchDog),
                    coffee: String(gasStationService.coffee),
                    bakery: String(gasStationService.bakery),
                    coffee_zone: String(gasStationService.coffee_zone),
                    terminal: String(gasStationService.terminal),
                    atm: String(gasStationService.atm),
                    bycicleCorner: String(gasStationService.bicycleCorner),
                    plasticDispose: String(gasStationService.plasticDispose),
                    batteryDispose: String(gasStationService.batteryDispose),
                    bookTrade: String(gasStationService.bookTrade),
                    takeClothes: String(gasStationService.takeClothes),
                    vulcanization: String(gasStationService.vulcanization),
                    toilet: String(gasStationService.toilet),
                    placeForPraying: String(gasStationService.placeForPraying),
                    condomate: String(gasStationService.condomate),
                    socGs: String(gasStationService.socGs),
                    kfcAuto: String(gasStationService.kfcAuto),
                    freeWifi: String(gasStationService.freeWifi)
                }
            };

            if (mode === "edit") {
                await updateStation(initialData.id, requestData);
                toast.success("Изменения станции сохранены", {
                    position: "bottom-left",
                    autoClose: 3000
                });
            } else {
                await createStation(requestData);
                toast.success("Станция успешно создана", {
                    position: "bottom-left",
                    autoClose: 3000
                });
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Ошибка сохранения:", error);
            toast.error(`Ошибка: ${error.response?.data?.message || error.message}`, {
                position: "bottom-left",
                autoClose: 3000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    return (
        <div className="modal-overlay">
            <div className="form">
                <div className="form-header">
                    <h1 className="form-title">
                        {mode === "edit" ? "Редактирование станции" : "Создание новой станции"}
                    </h1>
                    <button className="close-button" onClick={onClose}>
                        <img src={CloseIcon} alt="Закрыть" />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <h2 className="form-section-title">Основная информация станции</h2>

                    <div className="form-group">
                        <label className="field-label">Название станции</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите название станции"
                            value={gsSerial}
                            onChange={(e) => setGsSerial(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label className="field-label">Город</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите город"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label className="field-label">Адрес</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите адрес"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label className="field-label">Улица</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите улицу"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label className="field-label">Номер дома</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите номер дома"
                            value={streetNumber}
                            onChange={(e) => setStreetNumber(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label className="field-label">Широта</label>
                        <input
                            type="number"
                            className="text-input"
                            placeholder="Введите широту"
                            value={latitude}
                            onChange={(e) => setLatitude(e.target.value)}
                            step="any"
                        />
                    </div>

                    <div className="form-group">
                        <label className="field-label">Долгота</label>
                        <input
                            type="number"
                            className="text-input"
                            placeholder="Введите долготу"
                            value={longitude}
                            onChange={(e) => setLongitude(e.target.value)}
                            step="any"
                        />
                    </div>

                    <h2 className="form-section-title">Услуги</h2>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="isStoreAvailable">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="isStoreAvailable"
                                checked={isStoreAvailable}
                                onChange={(e) => setIsStoreAvailable(e.target.checked)}
                            />
                            <span className="checkbox-label">Магазин</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="isLpgAvailable">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="isLpgAvailable"
                                checked={isLpgAvailable}
                                onChange={(e) => setIsLpgAvailable(e.target.checked)}
                            />
                            <span className="checkbox-label">Газ</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="isBpowerAvailable">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="isBpowerAvailable"
                                checked={isBpowerAvailable}
                                onChange={(e) => setIsBpowerAvailable(e.target.checked)}
                            />
                            <span className="checkbox-label">Bpower</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="is100Available">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="is100Available"
                                checked={is100Available}
                                onChange={(e) => setIs100Available(e.target.checked)}
                            />
                            <span className="checkbox-label">АИ-100</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="isAi95Available">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="isAi95Available"
                                checked={isAi95Available}
                                onChange={(e) => setIsAi95Available(e.target.checked)}
                            />
                            <span className="checkbox-label">АИ-95</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="isAi92Available">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="isAi92Available"
                                checked={isAi92Available}
                                onChange={(e) => setIsAi92Available(e.target.checked)}
                            />
                            <span className="checkbox-label">АИ-92</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="isDtAvailable">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="isDtAvailable"
                                checked={isDtAvailable}
                                onChange={(e) => setIsDtAvailable(e.target.checked)}
                            />
                            <span className="checkbox-label">ДТ (Дизель)</span>
                        </label>
                    </div>

                    <h2 className="form-section-title">Дополнительные услуги</h2>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="frenchDog">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="frenchDog"
                                checked={gasStationService.frenchDog}
                                onChange={(e) => setGasStationService({ ...gasStationService, frenchDog: e.target.checked })}
                            />
                            <span className="checkbox-label">Френчдог</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="coffee">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="coffee"
                                checked={gasStationService.coffee}
                                onChange={(e) => setGasStationService({ ...gasStationService, coffee: e.target.checked })}
                            />
                            <span className="checkbox-label">Кофе</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="bakery">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="bakery"
                                checked={gasStationService.bakery}
                                onChange={(e) => setGasStationService({ ...gasStationService, bakery: e.target.checked })}
                            />
                            <span className="checkbox-label">Пекарня</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="coffee_zone">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="coffee_zone"
                                checked={gasStationService.coffee_zone}
                                onChange={(e) => setGasStationService({ ...gasStationService, coffee_zone: e.target.checked })}
                            />
                            <span className="checkbox-label">Кофейная зона</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="terminal">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="terminal"
                                checked={gasStationService.terminal}
                                onChange={(e) => setGasStationService({ ...gasStationService, terminal: e.target.checked })}
                            />
                            <span className="checkbox-label">Терминал</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="atm">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="atm"
                                checked={gasStationService.atm}
                                onChange={(e) => setGasStationService({ ...gasStationService, atm: e.target.checked })}
                            />
                            <span className="checkbox-label">Банкомат</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="bicycleCorner">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="bicycleCorner"
                                checked={gasStationService.bicycleCorner}
                                onChange={(e) => setGasStationService({ ...gasStationService, bicycleCorner: e.target.checked })}
                            />
                            <span className="checkbox-label">Уголок для велосипедов</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="plasticDispose">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="plasticDispose"
                                checked={gasStationService.plasticDispose}
                                onChange={(e) => setGasStationService({ ...gasStationService, plasticDispose: e.target.checked })}
                            />
                            <span className="checkbox-label">Утилизация пластика</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="batteryDispose">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="batteryDispose"
                                checked={gasStationService.batteryDispose}
                                onChange={(e) => setGasStationService({ ...gasStationService, batteryDispose: e.target.checked })}
                            />
                            <span className="checkbox-label">Утилизация батареек</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="bookTrade">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="bookTrade"
                                checked={gasStationService.bookTrade}
                                onChange={(e) => setGasStationService({ ...gasStationService, bookTrade: e.target.checked })}
                            />
                            <span className="checkbox-label">Обмен книгами</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="takeClothes">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="takeClothes"
                                checked={gasStationService.takeClothes}
                                onChange={(e) => setGasStationService({ ...gasStationService, takeClothes: e.target.checked })}
                            />
                            <span className="checkbox-label">Прием одежды</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="vulcanization">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="vulcanization"
                                checked={gasStationService.vulcanization}
                                onChange={(e) => setGasStationService({ ...gasStationService, vulcanization: e.target.checked })}
                            />
                            <span className="checkbox-label">Вулканизация</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="toilet">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="toilet"
                                checked={gasStationService.toilet}
                                onChange={(e) => setGasStationService({ ...gasStationService, toilet: e.target.checked })}
                            />
                            <span className="checkbox-label">Туалет</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="placeForPraying">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="placeForPraying"
                                checked={gasStationService.placeForPraying}
                                onChange={(e) => setGasStationService({ ...gasStationService, placeForPraying: e.target.checked })}
                            />
                            <span className="checkbox-label">Намазкана</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="condomate">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="condomate"
                                checked={gasStationService.condomate}
                                onChange={(e) => setGasStationService({ ...gasStationService, condomate: e.target.checked })}
                            />
                            <span className="checkbox-label">Кондомат</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="socGs">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="socGs"
                                checked={gasStationService.socGs}
                                onChange={(e) => setGasStationService({ ...gasStationService, socGs: e.target.checked })}
                            />
                            <span className="checkbox-label">Социальная заправка</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="kfcAuto">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="kfcAuto"
                                checked={gasStationService.kfcAuto}
                                onChange={(e) => setGasStationService({ ...gasStationService, kfcAuto: e.target.checked })}
                            />
                            <span className="checkbox-label">KFC Auto</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-container" htmlFor="freeWifi">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id="freeWifi"
                                checked={gasStationService.freeWifi}
                                onChange={(e) => setGasStationService({ ...gasStationService, freeWifi: e.target.checked })}
                            />
                            <span className="checkbox-label">Бесплатный Wi-Fi</span>
                        </label>
                    </div>

                    <div className="form-buttons">
                        <button type="button" className="cancel-button" onClick={onClose}>
                            Отменить
                        </button>
                        <button type="submit" className="save-button" disabled={!isFormValid || isSubmitting}>
                            Сохранить
                        </button>
                    </div>
                </form>
                {isSubmitting && (
                    <div className="loader-overlay">
                        <Loader />
                        <p>Сохранение станции...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StationForm;